import { i18n } from "@lingui/core";
import { card as warpCard, clickable } from "@warp-ds/css/component-classes";
import WarpElement from "@warp-ds/elements-core";
import { css, html, TemplateResult } from "lit";
import { trackClickEvent } from "../tracking.js";
import { Brand, Subvertical } from "../../constants.js";
import { TrackingEventMap } from "../../models/trackingEvent.js";
import { activateI18n } from "../../utils/i18n.js";
import { getToolsForTab, type ToolsTab } from "../../shared/tools-config.js";

interface CardWithIcon {
    icon: TemplateResult<1>;
    href: string;
    title: string;
    textStyle?: string;
    description: string;
    track?: (destination: string) => void;
}

interface Button {
    title: string;
    href: string;
    track: (destination: string) => void;
}

type Card = CardWithIcon;

class UsefulTools extends WarpElement {
    declare baseurl: string;
    declare subVertical: Subvertical;
    declare trackingEventMap: TrackingEventMap;
    declare brand: string;
    declare tab: ToolsTab;
    declare carIllustrationUrl: string;
    declare contractIllustrationUrl: string;

    static properties = {
        baseurl: { attribute: true, type: String },
        subVertical: { attribute: true, type: Subvertical },
        trackingEventMap: { type: Object },
        brand: { attribute: true, type: String },
        tab: { attribute: true, type: String },
        carIllustrationUrl: { attribute: true, type: String },
        contractIllustrationUrl: { attribute: true, type: String },
    };

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-V_731y;

            .description-container {
                grid-template-rows: subgrid;
                grid-row: span 3;
            }
        `,
    ];

    // User-defined type guard function for CardWithIcon
    isCardWithIcon(card: Card): card is CardWithIcon {
        return "icon" in card;
    }

    cardWithIcon(card: CardWithIcon, isExtended = false) {
        return html`
            <div class="flex-1 min-h-full ${warpCard.base} ${warpCard.shadow} ${warpCard.flatUnselected}">
                <div class="${warpCard.outline} ${warpCard.outlineUnselected}"></div>
                ${
                    isExtended
                        ? html`
                              <!-- Mobile layout: compact -->
                              <div class="p-16 grid gap-24 relative description-container md:hidden">
                                  ${card.icon}
                                  <div>
                                      <h4 class="t4 flex">
                                          <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                              <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                              ${card.title}
                                          </a>
                                          <w-icon name="ChevronRight" size="16px" class="flex items-center" locale="nb"></w-icon>
                                      </h4>
                                      <p class="text-body">${card.description}</p>
                                  </div>
                              </div>
                              <!-- Desktop layout: extended -->
                              <div class="p-16 relative hidden md:flex md:items-center h-full">
                                  <div class="flex gap-24 items-center flex-1">
                                      ${card.icon}
                                      <div class="flex-1">
                                          <h4 class="t4 flex mb-8">
                                              <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                                  <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                                  ${card.title}
                                              </a>
                                          </h4>
                                          <p class="text-body">${card.description}</p>
                                      </div>
                                      <div class="items-center self-center">
                                          <w-icon name="ChevronRight" size="16px" locale="sv"></w-icon>
                                      </div>
                                  </div>
                              </div>
                          `
                        : html`
                              <div class="p-16 grid gap-24 relative description-container">
                                  ${card.icon}
                                  <div>
                                      <h4 class="t4 flex">
                                          <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                              <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                              ${card.title}
                                          </a>
                                          <w-icon name="ChevronRight" size="16px" class="flex items-center" locale="sv"></w-icon>
                                      </h4>
                                      <p class="text-body">${card.description}</p>
                                  </div>
                              </div>
                          `
                }
            </div>
        `;
    }

    renderCards(cards: Card[]) {
        const isExtended = cards.length <= 2;
        return html`
            <div class="flex flex-col md:flex-row gap-16">
                ${cards.map((card: Card) => {
                    if (this.isCardWithIcon(card)) return this.cardWithIcon(card, isExtended);
                    return "";
                })}
            </div>
        `;
    }

    renderButton(button: Button) {
        return html`
            <w-card>
                <a
                    href="${button.href}"
                    aria-label="${button.title}"
                    class="${clickable.buttonOrLinkStretch}"
                    @click="${() => button.track(button.href)}"
                ></a>
                <w-button variant="link" class="grid s-bg place-items-center p-16">${button.title}</w-button>
            </w-card>
        `;
    }

    renderButtons(buttons: Button[]) {
        const cols = buttons.length;
        return html`
            <div class="flex flex-col md:grid md:grid-cols-${cols} gap-16 mb-56">${buttons.map((button) => this.renderButton(button))}</div>
        `;
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }
    render() {
        const title = i18n.t({
            id: "market-frontpage.useful-tools",
            message: "Useful tools",
        });

        const tools = getToolsForTab(this.brand as Brand, this.subVertical, this.tab === "sell" ? "sell" : "allCars");

        const cards: Card[] = tools.map((tool) => {
            const iconHtml =
                tool.icon === "CarIllustration"
                    ? html`<img src="${this.carIllustrationUrl}" alt="Blocket" />`
                    : tool.icon === "ContractIllustration"
                      ? html`<img src="${this.contractIllustrationUrl}" alt="Blocket" />`
                      : html`<w-icon
                            name="${tool.icon}"
                            size="32px"
                            class="block h-32"
                            locale="${this.brand === Brand.DBA ? "da" : this.brand === Brand.BLOCKET ? "sv" : "nb"}"
                        ></w-icon>`;
            // for some reason UnoCSS doesn't see the "s-bg-primary-subtle" class unless it's specified in this file
            const iconBackground = tool.iconBackground === "s-bg-primary-subtle" ? "s-bg-primary-subtle" : tool.iconBackground;

            return {
                icon: html`<div class="h-[100] w-[100] rounded-full grid place-items-center ${iconBackground}">${iconHtml}</div>`,
                href: tool.route,
                title: tool.header,
                description: tool.body ? tool.body : "",
                track: (destination: string) => trackClickEvent(this.trackingEventMap[tool.trackingNumber], destination),
            };
        });

        return html`
            <div class="flex flex-col gap-16">
                <h3 class="t3 mb-0">${title}</h3>
                ${this.renderCards(cards)}
            </div>
        `;
    }
}

customElements.define("useful-tools", UsefulTools as unknown as CustomElementConstructor);
